import { useMemo } from 'react';
import { useAuth } from "../../../utils/Auth";
import { useNavigate } from 'react-router-dom';
import QrRef from '../QrRef';
import { useWindowSize } from '../../../utils/hooks/useWindowSize';
import { motion, AnimatePresence } from 'motion/react';
import { variants, transition } from './variants';
import styles from './QrChildren.module.css';

export default function QrChildren() {

    const { qr_data, liquidQRs } = useAuth();

    const linkedQrs = useMemo(() => liquidQRs?.filter(qr => qr?.data?.linked_qr), [liquidQRs]);
    const hasChildren = useMemo(() => qr_data?.data?.children?.length > 0, [qr_data?.data?.children]);
    const childQrs = useMemo(() => linkedQrs?.filter(child => child?.data?.linked_qr === qr_data?.id), [linkedQrs, hasChildren, qr_data?.id]);

    const handleReturn = useMemo(() => {
        if (hasChildren) {
            return (
                <>
                {childQrs.map(qr => (
                    <Child qr={qr} key={qr?.id} />
                ))}
                <PseudoQrs qrCount={childQrs?.length} />
                </>
            );
        } else {
            return <div className={styles.noChildren}>This QR has no associated QRs.</div>
        }
    },[hasChildren, childQrs]);

    return (
        <div className={styles.container}>
            <div className={styles.header}>Linked QRs</div>
            {hasChildren && (
                <div className={styles.words}>
                    These linked QRs are like individual entryways all leading to a single main destination. 
                    They provide a simple, at-a-glance solution for scanning, while still guiding every user to the same place as the main QR.
                </div>
            )}
            <motion.div 
                className={hasChildren ? styles.containerInnerQrs : styles.containerInnerWords}
                transition={transition}
                variants={variants}
                initial={false}
                
            >
                {handleReturn}
            </motion.div>
        </div>
    );
};

const Child = ({qr}) => {

    const navigate = useNavigate();
    const childQrOptions = useMemo(() => qr?.data?.qr_style, [qr?.data?.qr_style]);
    const nickname = useMemo(() => qr?.data?.nickname, [qr?.data?.nickname]);
    const total = useMemo(() => {
        const scanCount = qr?.data?.scanCount_cache || 0;
        return scanCount;
    },[qr?.data?.scanCount_cache]);

    const handleViewDetails = () => {
        navigate(`/collection/${qr?.id}/details?tab=details`);
    };

    return (
        <div className={styles.childContainer}>
            <QrRef size={150} childQrOptions={childQrOptions} />
            <h5 className={styles.nickname}>{nickname}</h5>
            <div className={styles.infoWrap}>
                <div className={styles.count}><i className='fas fa-eye' />{total}</div>
                <div className={styles.qrId}>ID: {qr?.id}</div>
            </div>
            <button className={styles.detailsBtn} onClick={handleViewDetails}>Details</button>
        </div>
    );
};

const PseudoQrs = ({qrCount}) => {
    const { width } = useWindowSize();

    const itemsPerRow = useMemo(() => {
        if (width > 1080) {
            return 5;
        } else if (width > 992) {
            return 4;
        } else if (width > 768) {
            return 3;
        } else if (width > 500) {
            return 2;
        } else return 1;
    }, [width, qrCount]);

    const pseudoQrCount = qrCount % itemsPerRow === 0 ? 0 : itemsPerRow - (qrCount % itemsPerRow);

    return (
        <>
        {Array.from({ length: pseudoQrCount }).map((_, index) => (
            <div key={`pseudo-${index}`} className={styles.pseudoChild}>
                <i className={`fa-sharp fa-qrcode ${styles.pseudoCode}`} />
                <h5 className={styles.nickname}>Pseudo QR</h5>
                <div className={styles.infoWrap}>
                    <div className={styles.count}><i className='fas fa-eye' />0</div>
                    <div className={styles.qrId}>ID: 000000</div>
                </div>
                <div className={styles.pseudoBtn}>Details</div>
            </div>
        ))}
        </>
    );
};